import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Copy, Tag } from "lucide-react";
import { toast } from "sonner";
import { scrollToHash } from "./Header";

const SEEN_KEY = "phudg_offer_seen";

export const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    if (location.pathname.startsWith("/cart") || location.pathname.startsWith("/admin") || location.pathname.startsWith("/offer")) return;

    let triggered = false;
    const fire = () => {
      if (triggered || localStorage.getItem(SEEN_KEY)) return;
      triggered = true;
      localStorage.setItem(SEEN_KEY, "1");
      setShow(true);
    };
    // desktop: cursor leaves the page (exit intent); mobile/fallback: 30s or 60% scroll
    const onLeave = (e) => {
      if (e.clientY <= 8) fire();
    };
    const onScroll = () => {
      const h = document.documentElement;
      if (h.scrollHeight - h.clientHeight > 0 && h.scrollTop / (h.scrollHeight - h.clientHeight) > 0.6) fire();
    };
    const timer = setTimeout(fire, 30000);
    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", onScroll);
      clearTimeout(timer);
    };
  }, [location.pathname]);

  const close = () => setShow(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText("FIRSTPHUDG");
      toast.success("Code copied — apply it at checkout");
    } catch {
      toast.success("Code: FIRSTPHUDG — apply it at checkout");
    }
  };

  const shopNow = () => {
    setShow(false);
    if (location.pathname !== "/") {
      sessionStorage.setItem("scrollToHash", "#oils");
      navigate("/");
    } else {
      scrollToHash("#oils");
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="exit-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/70 backdrop-blur-sm px-5"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-3xl bg-ink grain border border-gold/30 p-8 md:p-10 text-center"
          >
            <button
              data-testid="exit-popup-close-btn"
              onClick={close}
              aria-label="Close offer"
              className="absolute top-4 right-4 text-cream/40 hover:text-gold transition-colors"
            >
              <X size={20} />
            </button>
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-3">First time here?</p>
            <h3 className="font-serif text-3xl md:text-4xl text-cream tracking-tight mb-3">
              Get <span className="italic text-gold">10% OFF</span> your first PhudG order
            </h3>
            <p className="text-sm font-light text-cream/60 mb-6">
              Free delivery on every order — pay online for an extra 5% off, and bundle 2+ one-litre bottles to save 12%, 4+ to save 15%.
            </p>
            <div className="flex items-center justify-center gap-3 mb-7">
              <div className="flex items-center gap-2 rounded-full border border-dashed border-gold/60 px-6 py-3">
                <Tag size={14} className="text-gold" />
                <span className="font-serif text-xl tracking-[0.2em] text-cream">FIRSTPHUDG</span>
              </div>
              <button
                data-testid="exit-popup-copy-btn"
                onClick={copyCode}
                className="flex items-center gap-1.5 rounded-full border border-cream/20 px-4 py-3 text-xs text-cream/70 hover:border-gold hover:text-gold transition-colors"
              >
                <Copy size={13} /> Copy
              </button>
            </div>
            <motion.button
              data-testid="exit-popup-shop-btn"
              onClick={shopNow}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="w-full rounded-full bg-gold px-8 py-4 text-sm font-medium text-ink hover:bg-cream transition-colors duration-300"
            >
              Claim 10% Off — Shop the Oils
            </motion.button>
            <button
              data-testid="exit-popup-dismiss-btn"
              onClick={close}
              className="mt-4 text-xs text-cream/40 hover:text-cream/70 transition-colors underline underline-offset-4"
            >
              No thanks, I'll pay full price
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
